import { Component, DestroyRef, OnInit } from '@angular/core';
import { API, TemplateDataSetDTO, TemplateGroupDTO } from '../../../../services/api/api';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { UtilService } from '../../../../services/util.service';
import { NavigationService } from '@ngx-seatback/utils';
import { QueryParamService } from '../../../../services/lists/query-params';
import { TranslateService } from '@ngx-translate/core';
import { PermissionService } from '../../../../services/permission.service';
import { RoutingID } from '../../../../routes/sb-routing-enum';
import { TemplateGroupsComponent } from '../../templateGroup/template-groups.component';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';

@Component({
  selector: 'sb-page-template-dataset-edit',
  templateUrl: './template-dataset-edit.component.html',
  styleUrls: ['./template-dataset-edit.component.scss'],
  standalone: false,
})
export class TemplateDatasetEditComponent implements OnInit {
  templateDataset: TemplateDataSetDTO;
  templateGroup: TemplateGroupDTO;
  templateDatasetId: number;
  loading: boolean;
  form: UntypedFormGroup;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly api: API,
    private readonly translate: TranslateService,
    private readonly destroyRef: DestroyRef,
    public errorHandler: ErrorService,
    public modal: ModalService,
    public util: UtilService,
    public navService: NavigationService,
    public queryParamService: QueryParamService,
    public permissionService: PermissionService
  ) {
    this.templateGroup = undefined;

    this.templateDatasetId = null;
    this.loading = true;
    this.form = new UntypedFormGroup({
      Name: new UntypedFormControl('', [Validators.required]),
    });
  }

  get Name() {
    return this.form.get('Name');
  }

  ngOnInit(): void {
    this.registerFormChangesListener();
    this.route.params.subscribe((params: Params) => {
      this.templateDatasetId = params.templateDatasetID;
      this.refreshFromServer();
    });
    this.refreshFromServer();
  }

  private registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      if (this.templateDataset) {
        this.templateDataset.name = value;
      }
    });
  }

  updateForm() {
    this.Name.setValue(this.templateDataset.name);
    this.Name.markAsTouched();
  }

  private refreshFromServer() {
    this.loading = true;
    if (!this.templateDatasetId) {
      this.templateDataset = new TemplateDataSetDTO();
      this.loading = false;
    } else {
      this.api
        .mm_templating_advertiser_TemplateDataSet_retrieveById(this.templateDatasetId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (templateDataset: TemplateDataSetDTO) => {
            this.templateDataset = templateDataset;
            this.templateGroup = this.templateDataset.group;
            this.updateForm();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  createTemplateDataset() {
    if (this.templateDataset.name && this.templateGroup?.id !== -1) {
      this.loading = true;
      this.api
        .mm_templating_provider_TemplateGroup_retrieveById(this.templateGroup.id)
        .pipe(
          switchMap((templateGroup: TemplateGroupDTO) => {
            this.templateDataset.group = templateGroup;
            return this.api.mm_templating_advertiser_TemplateDataSet_create(this.templateDataset);
          }),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (createdTemplateDataset: TemplateDataSetDTO) => {
            this.templateDataset = createdTemplateDataset;
            this.navService.navigateToPoint(RoutingID.templateDatasets, null);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  updateTemplateDataset() {
    if (this.templateDataset.name && this.templateGroup?.id !== -1) {
      this.loading = true;
      this.api
        .mm_templating_provider_TemplateGroup_retrieveById(this.templateGroup.id)
        .pipe(
          switchMap((templateGroup: TemplateGroupDTO) => {
            this.templateDataset.group = templateGroup;
            return this.api.mm_templating_advertiser_TemplateDataSet_edit(this.templateDataset);
          }),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (editedTemplateDataset: TemplateDataSetDTO) => {
            this.templateDataset = editedTemplateDataset;
            this.navService.navigateToPoint(RoutingID.templateDatasets, null);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  selectGroup() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: TemplateGroupsComponent, data: { selectedMedium: this.templateGroup } },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then(result => {
        if (result) {
          this.templateGroup = result as TemplateGroupDTO;
        }
      })
      .catch(() => {});
  }

  getFilterSelectClass() {
    if (this.templateGroup) {
      return 'btn-hover-primary--active';
    }
    return 'btn-hover-primary';
  }

  getFilterText() {
    if (this.templateGroup) {
      return this.templateGroup.name;
    }
    return this.translate.instant('lang.globals.templateGroup');
  }
}
